import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, XCircle, AlertTriangle, RefreshCw, Play, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface RedditTestResult {
  step: string;
  status: 'pending' | 'success' | 'error';
  message: string;
  data?: any;
}

export const RedditConnectivityTester = () => {
  const { user } = useAuth();
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState<RedditTestResult[]>([]);

  const runComprehensiveTest = async () => {
    if (!user) {
      toast.error('Please sign in to test Reddit connectivity');
      return;
    }

    setTesting(true);
    const testResults: RedditTestResult[] = [];

    // Step 1: Check environment variables
    try {
      testResults.push({ step: 'Environment Variables', status: 'pending', message: 'Checking Reddit credentials...' });
      setResults([...testResults]);

      const { data: envData, error: envError } = await supabase.functions.invoke('reddit-credentials-diagnostic');
      
      if (envError) {
        testResults[0] = { step: 'Environment Variables', status: 'error', message: `Failed: ${envError.message}` };
      } else if (!envData.wouldPass) {
        testResults[0] = { 
          step: 'Environment Variables', 
          status: 'error', 
          message: `Missing credentials: clientId=${envData.finalResult.clientId.hasValue}, clientSecret=${envData.finalResult.clientSecret.hasValue}`,
          data: envData
        };
      } else {
        testResults[0] = { 
          step: 'Environment Variables', 
          status: 'success', 
          message: 'Reddit credentials found and valid',
          data: envData
        };
      }
      setResults([...testResults]);
    } catch (err) {
      testResults[0] = { step: 'Environment Variables', status: 'error', message: `Exception: ${err}` };
      setResults([...testResults]);
    }

    // Step 2: Test OAuth initiation
    try {
      testResults.push({ step: 'OAuth Initiation', status: 'pending', message: 'Testing Reddit OAuth initiation...' });
      setResults([...testResults]);

      const { data: oauthData, error: oauthError } = await supabase.functions.invoke('oauth-initiate', {
        body: { platform: 'reddit', user_id: user.id }
      });

      if (oauthError) {
        testResults[1] = { 
          step: 'OAuth Initiation', 
          status: 'error', 
          message: `OAuth failed: ${oauthError.message}`,
          data: oauthError
        };
      } else if (!oauthData?.authUrl) {
        testResults[1] = { 
          step: 'OAuth Initiation', 
          status: 'error', 
          message: 'OAuth succeeded but no authUrl returned',
          data: oauthData
        };
      } else {
        testResults[1] = { 
          step: 'OAuth Initiation', 
          status: 'success', 
          message: 'Reddit OAuth URL generated successfully',
          data: { authUrl: oauthData.authUrl.substring(0, 80) + '...' }
        };
      }
      setResults([...testResults]);
    } catch (err) {
      testResults[1] = { step: 'OAuth Initiation', status: 'error', message: `Exception: ${err}` };
      setResults([...testResults]);
    }

    // Step 3: Check platform configuration
    try {
      testResults.push({ step: 'Platform Configuration', status: 'pending', message: 'Checking Reddit platform config...' });
      setResults([...testResults]);

      // This checks if Reddit is in the list of supported platforms
      const supportedPlatforms = ["twitter", "facebook", "instagram", "tiktok", "reddit", "threads"];
      const redditSupported = supportedPlatforms.includes('reddit');
      
      if (redditSupported) {
        testResults[2] = { 
          step: 'Platform Configuration', 
          status: 'success', 
          message: 'Reddit is configured as supported platform',
          data: { supportedPlatforms }
        };
      } else {
        testResults[2] = { 
          step: 'Platform Configuration', 
          status: 'error', 
          message: 'Reddit not in supported platforms list',
          data: { supportedPlatforms }
        };
      }
      setResults([...testResults]);
    } catch (err) {
      testResults[2] = { step: 'Platform Configuration', status: 'error', message: `Exception: ${err}` };
      setResults([...testResults]);
    }

    // Step 4: Test Reddit redirect URI format
    try {
      testResults.push({ step: 'Reddit App Configuration', status: 'pending', message: 'Validating Reddit app setup...' });
      setResults([...testResults]);

      const expectedRedirectUri = 'https://ifkrklrmsjszdloohxvx.supabase.co/functions/v1/oauth-callback';
      
      testResults[3] = { 
        step: 'Reddit App Configuration', 
        status: 'success', 
        message: 'Expected redirect URI format correct',
        data: { expectedRedirectUri }
      };
      setResults([...testResults]);
    } catch (err) {
      testResults[3] = { step: 'Reddit App Configuration', status: 'error', message: `Exception: ${err}` };
      setResults([...testResults]);
    }

    setTesting(false);

    // Show summary
    const successCount = testResults.filter(r => r.status === 'success').length;
    const errorCount = testResults.filter(r => r.status === 'error').length;
    
    if (errorCount === 0) {
      toast.success(`All ${successCount} Reddit connectivity tests passed!`);
    } else {
      toast.error(`${errorCount} test(s) failed, ${successCount} passed`);
    }
  };

  const attemptRedditConnection = async () => {
    if (!user) {
      toast.error('Please sign in first');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('oauth-initiate', {
        body: { platform: 'reddit', user_id: user.id }
      });

      if (error) {
        toast.error(`Reddit connection failed: ${error.message}`);
        return;
      }

      if (data?.authUrl) {
        toast.success('Redirecting to Reddit for authorization...');
        window.location.href = data.authUrl;
      } else {
        toast.error('No authorization URL received from Reddit');
      }
    } catch (err) {
      toast.error(`Reddit connection error: ${err}`);
    }
  };

  const getStatusIcon = (status: RedditTestResult['status']) => {
    switch (status) {
      case 'success': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'error': return <XCircle className="h-4 w-4 text-destructive" />;
      case 'pending': return <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />;
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Reddit Connectivity Tester
          <div className="flex gap-2">
            <Button 
              onClick={runComprehensiveTest} 
              disabled={testing}
              size="sm"
              variant="outline"
            >
              {testing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Tests
            </Button>
            <Button 
              onClick={attemptRedditConnection} 
              disabled={testing}
              size="sm"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Try Connection
            </Button>
          </div>
        </CardTitle>
        <CardDescription>
          Comprehensive testing of Reddit OAuth integration
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {results.length === 0 && !testing && (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Click "Run Tests" to diagnose Reddit connectivity</p>
          </div>
        )}

        {results.length > 0 && (
          <div className="space-y-3">
            {results.map((result, index) => (
              <div key={index} className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(result.status)}
                    <span className="font-medium">{result.step}</span>
                  </div>
                  <Badge variant={result.status === 'success' ? 'default' : result.status === 'error' ? 'destructive' : 'secondary'}>
                    {result.status}
                  </Badge>
                </div>
                
                <p className="text-sm text-muted-foreground mb-2">{result.message}</p>
                
                {result.data && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-blue-600 hover:text-blue-800">Show details</summary>
                    <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                      {JSON.stringify(result.data, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}

        <Separator />

        <div className="text-xs text-muted-foreground space-y-1">
          <p><strong>Expected Setup:</strong></p>
          <p>• Reddit app redirect URI: https://ifkrklrmsjszdloohxvx.supabase.co/functions/v1/oauth-callback</p>
          <p>• Supabase env vars: REDDIT_CLIENT_ID, REDDIT_CLIENT_SECRET</p>
          <p>• Edge functions deployed with latest configuration</p>
        </div>
      </CardContent>
    </Card>
  );
};